import { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { Button } from "@heroui/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  PricingSelectionStep,
  type PricingStepHandle,
} from "./steps/pricing-selection-step";
import { CreateShell } from "./create-order-layout";
import { useCreateOrder } from "./create-order-context";
import { DraftSavedIndicator } from "./draft-saved-indicator";
import type { OrderDraft } from "./types";
import { useTranslation } from "react-i18next";
import { totalWeightKg } from "@/lib/unit-display";

function RouteSummary({ data }: { data: OrderDraft }) {
  const { t } = useTranslation("app");
  const shipment = data.shipment;
  if (!shipment) return null;
  const fromCity = `${shipment.shipFrom.city}, ${shipment.shipFrom.state}`;
  const fromPostal = shipment.shipFrom.postalCode;
  const toCity = `${shipment.shipTo.city}, ${shipment.shipTo.state}`;
  const toPostal = shipment.shipTo.postalCode;
  const packageCount = shipment.packages.length;
  const totalWeight = totalWeightKg(shipment.packages).toFixed(1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_auto] gap-6 items-center px-6 py-5 rounded-2xl bg-default-50 mb-6">
      <div>
        <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
          {t("createShipment.from")}
        </div>
        <div className="text-base font-semibold mt-1">{fromCity}</div>
        <div className="text-xs text-default-500 font-mono">{fromPostal}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-default-400 hidden md:block" />
      <div>
        <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
          {t("createShipment.to")}
        </div>
        <div className="text-base font-semibold mt-1">{toCity}</div>
        <div className="text-xs text-default-500 font-mono">{toPostal}</div>
      </div>
      <div className="hidden md:block w-px h-10 bg-default-200" />
      <div className="flex gap-5">
        <div>
          <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
            {t("createShipment.packages")}
          </div>
          <div className="text-base font-semibold mt-1 font-mono">
            {packageCount} · {totalWeight} kg
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreateShipmentPricingScreen() {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const { data, update } = useCreateOrder();
  const stepRef = useRef<PricingStepHandle>(null);
  const [canSubmit, setCanSubmit] = useState(false);

  const handleNext = (stepData: Partial<OrderDraft>) => {
    update(stepData);
    void navigate("/new-shipment/payment");
  };

  // Render-time guard: can't price without the details-step data.
  if (!data.shipment) return <Navigate to="/new-shipment/details" replace />;

  return (
    <CreateShell
      step={2}
      title={t("createShipment.pricingTitle")}
      italic={t("createShipment.pricingItalic")}
      lede={t("createShipment.pricingLede")}
      footer={
        <>
          <div className="hidden md:flex items-center gap-4">
            <DraftSavedIndicator />
          </div>
          <div className="flex gap-2 md:gap-3 w-full md:w-auto">
            <Button
              variant="light"
              startContent={<ArrowLeft className="h-4 w-4" />}
              className="hidden md:inline-flex"
              onPress={() => navigate("/new-shipment/details")}
            >
              {t("createShipment.backToDetails")}
            </Button>
            <Button
              color="primary"
              fullWidth
              className="md:w-auto"
              endContent={<ArrowRight className="h-4 w-4" />}
              isDisabled={!canSubmit}
              onPress={() => stepRef.current?.submit()}
            >
              {t("createShipment.continueToPayment")}
            </Button>
          </div>
        </>
      }
    >
      <RouteSummary data={data} />
      <PricingSelectionStep
        ref={stepRef}
        data={data}
        onNext={handleNext}
        onEditDetails={(party) =>
          void navigate(`/new-shipment/details#${party.toLowerCase()}`)
        }
        onCanSubmitChange={setCanSubmit}
      />
    </CreateShell>
  );
}
